import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ImageComponent } from '@/components/Image';
import { getReactAppApiUrl } from '@/helpers/conf';
import { saveLocalNDBFlow } from '@/helpers/NDBFlow';
import { IS_GOOGLE_AUTH_HIDDEN } from '@/helpers/oauth';
import { useIsLanding } from '@/hooks/useIsLanding';
import { useNDBFlow } from '@/hooks/useNDBFlow';
import type { IAuthProvider } from '@/modules/common/hooks/useAuthProviders';
import {
  ANROID_AND_IOS_APP_SOURCE,
  useTrackingUserParamsStore,
} from '@/modules/userTrackingParams';
import {
  setGoogleOAuthAffiliateParams,
  useAffiliateRegConfig,
} from '../../modules/promo';
import { useRegModalTheme } from '../../modules/registration';
import { Button } from '../buttons/Button';
import { Divider } from './components/Divider';
import { ScContinueWithGoogle } from './styled';

export interface ContinueWithGoogleProps {
  dividerGap?: 'small' | 'large';
  marginBottom?: 'small' | 'large';
  onSendGAEvent: () => void;
}

const GOOGLE_PROVIDER_OPTION: IAuthProvider = {
  name: 'google_oauth2',
  url: '/users/auth/google_oauth2',
};

export const ContinueWithGoogle = ({
  dividerGap = 'large',
  marginBottom = 'large',
  onSendGAEvent,
}: ContinueWithGoogleProps) => {
  const { t } = useTranslation('common');
  const { isLanding } = useIsLanding();
  const { buttons } = useRegModalTheme();
  const { isNDBFlow } = useNDBFlow();

  const { source } = useTrackingUserParamsStore(
    useShallow((state) => ({ source: state.source }))
  );

  const affiliateConfig = useAffiliateRegConfig();
  const { googleAuth } = affiliateConfig;

  const handleRedirectToAuthProvider = useCallback(() => {
    const origin = getReactAppApiUrl(window.location.hostname);
    onSendGAEvent();

    if (isLanding && isNDBFlow) {
      saveLocalNDBFlow({ isPendingOAuthNDBRedirect: true });
    }

    if (isLanding) {
      const { regModalTheme: _, ...checkboxParams } = affiliateConfig;
      setGoogleOAuthAffiliateParams(checkboxParams);
    }

    window.location.href = origin + GOOGLE_PROVIDER_OPTION.url;
  }, [affiliateConfig, isLanding, isNDBFlow, onSendGAEvent]);

  const isWebview = source === ANROID_AND_IOS_APP_SOURCE;

  if ((isLanding && !googleAuth) || IS_GOOGLE_AUTH_HIDDEN || isWebview)
    return null;

  return (
    <ScContinueWithGoogle
      dividerGap={dividerGap}
      isGoogleAuth={googleAuth}
      marginBottom={marginBottom}
    >
      {googleAuth && <Divider />}
      <Button
        {...buttons?.googleRegButton?.colorMode}
        borderRadius={buttons?.googleRegButton?.borderRadius}
        onClick={handleRedirectToAuthProvider}
        testId="google-oauth-button"
        wide
      >
        <ImageComponent
          alt="google icon"
          className="image"
          height={24}
          src="/common/googleIcon.webp"
          width={24}
        />
        {t('modals.authorization.google')}
      </Button>
      {!googleAuth && <Divider />}
    </ScContinueWithGoogle>
  );
};
